/**
 * ResultMedia - the <video> or <audio> element a result screen plays a finished
 * job's output with.
 *
 * The URL it plays is often a presigned S3 link, which dies an hour after it
 * was signed while the result stays on screen for up to a day. When the element
 * reports an error it asks for a freshly signed link (`renewUrl`, which re-reads
 * the job: the backend re-signs its links on every read) and carries on playing
 * from where it was. If the renewed link fails too, or none can be obtained, it
 * says so instead of leaving a dead player whose play button does nothing.
 *
 * A link that cannot expire (a public CDN or GCS URL, a data: or blob: URL)
 * comes without `renewUrl`; an error on it is only logged.
 *
 * The URL goes on the element's `src`, never on a <source> child: a failing
 * <source> fires its error on the <source> element, so a listener on the media
 * element never hears about it and the failure is silent.
 *
 * The URL is used exactly as given. A presigned SigV4 URL signs its whole query
 * string, so anything appended to it (the single-clip player once added a `_cb`
 * cache-buster) makes S3 answer 403 SignatureDoesNotMatch.
 */

import React, { forwardRef, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

export interface ResultMediaProps {
  kind: 'video' | 'audio';
  /** Absolute URL of the media to play. */
  url: string;
  /**
   * Resolve a freshly signed URL for the same media; rejects when none can be
   * had. The caller also stores it, so it comes back as the new `url`: the
   * element never keeps a URL of its own. Omit for links that cannot expire.
   */
  renewUrl?: () => Promise<string>;
  className?: string;
  controls?: boolean;
  autoPlay?: boolean;
  preload?: 'none' | 'metadata' | 'auto';
  controlsList?: string;
  'data-testid'?: string;
  /** Fallback content for browsers that cannot play the element. */
  children?: React.ReactNode;
}

export const ResultMedia = forwardRef<HTMLMediaElement, ResultMediaProps>(function ResultMedia(
  {
    kind,
    url,
    renewUrl,
    className,
    controls,
    autoPlay,
    preload,
    controlsList,
    'data-testid': testId,
    children,
  },
  ref
) {
  const { t } = useTranslation();
  // The URL that could not be played. Keyed by URL so a new one (a new result,
  // or a renewal) is always given its chance.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // The URL the last renewal returned, and the last URL that actually loaded.
  // A renewed URL that errors before it ever loads is a real failure, not an
  // expiry -- renewing again would loop.
  const renewedUrlRef = useRef<string | null>(null);
  const loadedUrlRef = useRef<string | null>(null);
  const isRenewingRef = useRef(false);
  const wantsPlaybackRef = useRef(false);
  const resumeAtRef = useRef(0);

  const handleError = async (media: HTMLMediaElement): Promise<void> => {
    console.error(`Result ${kind} loading error:`, media.error, 'URL:', url);
    if (!renewUrl || isRenewingRef.current) return;
    if (url === renewedUrlRef.current && url !== loadedUrlRef.current) {
      setFailedUrl(url);
      return;
    }
    isRenewingRef.current = true;
    resumeAtRef.current = media.currentTime;
    try {
      const renewed = await renewUrl();
      if (renewed === url) {
        // Nothing new to try: the same link would fail the same way.
        setFailedUrl(url);
        return;
      }
      renewedUrlRef.current = renewed;
    } catch (error) {
      console.error(`Could not renew the ${kind} link:`, error);
      setFailedUrl(url);
    } finally {
      isRenewingRef.current = false;
    }
  };

  const handleLoadedMetadata = (media: HTMLMediaElement): void => {
    if (media.duration === 0 || isNaN(media.duration) || !isFinite(media.duration)) {
      console.error(`Invalid ${kind}: duration is 0 or NaN. URL:`, url);
      return;
    }
    loadedUrlRef.current = url;
    if (resumeAtRef.current > 0) {
      media.currentTime = resumeAtRef.current;
      resumeAtRef.current = 0;
    }
    if (wantsPlaybackRef.current) {
      media.play().catch((error: unknown) => console.warn('Could not resume playback:', error));
    }
  };

  if (failedUrl === url) {
    return (
      <div
        className="p-3 bg-amber-900/20 border border-amber-700 rounded-lg"
        data-testid="result-media-unavailable"
      >
        <div className="flex items-start space-x-2">
          <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-amber-200">{t('result.linkRenewal.title')}</p>
            <p className="text-amber-300 mt-1">{t('result.linkRenewal.message')}</p>
          </div>
        </div>
      </div>
    );
  }

  const mediaProps = {
    src: url,
    className,
    controls,
    autoPlay,
    preload,
    controlsList,
    'data-testid': testId,
    onPlay: () => {
      wantsPlaybackRef.current = true;
    },
    onPause: (e: React.SyntheticEvent<HTMLMediaElement>) => {
      // An error also pauses the element; only a pause the user asked for
      // (while the media is healthy) cancels the wish to keep playing.
      if (!e.currentTarget.error) wantsPlaybackRef.current = false;
    },
    onLoadedMetadata: (e: React.SyntheticEvent<HTMLMediaElement>) =>
      handleLoadedMetadata(e.currentTarget),
    onError: (e: React.SyntheticEvent<HTMLMediaElement>) => void handleError(e.currentTarget),
  };

  return kind === 'video' ? (
    <video ref={ref as React.Ref<HTMLVideoElement>} {...mediaProps}>
      {children}
    </video>
  ) : (
    <audio ref={ref as React.Ref<HTMLAudioElement>} {...mediaProps}>
      {children}
    </audio>
  );
});
